import type { FieldDefinition } from '../../utils/schemaBuilder';
import { getFieldTypeLabel } from '../../utils/schemaBuilder';

interface FieldListProps {
  fields: FieldDefinition[];
  onEdit: (field: FieldDefinition) => void;
  onDelete: (fieldId: string) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  readOnly?: boolean;
}

export function FieldList({ fields, onEdit, onDelete, readOnly = false }: FieldListProps) {
  if (fields.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="mt-2 text-sm">No fields defined</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">Click "Add Field" to define input fields</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {fields.map((field) => (
        <FieldItem
          key={field.id}
          field={field}
          onEdit={() => onEdit(field)}
          onDelete={() => onDelete(field.id)}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}

interface FieldItemProps {
  field: FieldDefinition;
  onEdit: () => void;
  onDelete: () => void;
  readOnly?: boolean;
}

function FieldItem({ field, onEdit, onDelete, readOnly = false }: FieldItemProps) {
  const hasEnum = field.enumValues && field.enumValues.length > 0;
  const typeLabel = getFieldTypeLabel(field.type, hasEnum, field.format);

  const getConstraints = (): string | null => {
    const parts: string[] = [];

    if (field.type === 'number' || field.type === 'integer') {
      if (field.minimum !== undefined && field.maximum !== undefined) {
        parts.push(`${field.minimum}-${field.maximum}`);
      } else if (field.minimum !== undefined) {
        parts.push(`min: ${field.minimum}`);
      } else if (field.maximum !== undefined) {
        parts.push(`max: ${field.maximum}`);
      }
    }

    if (field.type === 'string' && !hasEnum) {
      if (field.minLength !== undefined && field.maxLength !== undefined) {
        parts.push(`${field.minLength}-${field.maxLength} chars`);
      } else if (field.minLength !== undefined) {
        parts.push(`min: ${field.minLength} chars`);
      } else if (field.maxLength !== undefined) {
        parts.push(`max: ${field.maxLength} chars`);
      }
    }

    return parts.length > 0 ? parts.join(', ') : null;
  };

  const constraints = getConstraints();

  return (
    <div className={`flex items-start gap-3 p-3 border rounded-lg ${
      readOnly
        ? 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600'
        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 group'
    }`}>
      {/* Required indicator */}
      <div className="pt-0.5">
        {field.required ? (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400" title="Required">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
            </svg>
          </span>
        ) : (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500" title="Optional">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
            </svg>
          </span>
        )}
      </div>

      {/* Field info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-white truncate">
            {field.name}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
            {typeLabel}
          </span>
          {field.required && (
            <span className="text-xs text-red-500 dark:text-red-400">required</span>
          )}
        </div>

        {/* Description */}
        {field.description && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">
            {field.description}
          </p>
        )}

        {/* Enum values preview */}
        {hasEnum && (
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Options: {field.enumValues!.slice(0, 5).join(', ')}
            {field.enumValues!.length > 5 && ` +${field.enumValues!.length - 5} more`}
          </p>
        )}

        {/* Constraints */}
        {constraints && (
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {constraints}
          </p>
        )}
      </div>

      {/* Actions - hidden in read-only mode */}
      {!readOnly && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
            title="Edit field"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
            title="Delete field"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
